import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, Navigate, Link } from 'react-router-dom';
import api from '../../services/api';
import useAuthStore from '../../store/authStore';
import useQuizStore from '../../store/quizStore';
import useDashboardPrefs from '../../hooks/useDashboardPrefs';
import usePageTitle from '../../hooks/usePageTitle';
import Quiz from './Quiz';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import useFeaturePing from '../../hooks/useFeaturePing';
import trackEvent from '../../services/trackEvent';
import '../../css/guided-entry.css';
import Illustration from '../../components/ui/Illustration';
import illuMaitrise from '../../assets/illustrations/progression-maitrise.svg?raw';
import illuBilan from '../../assets/illustrations/bilan-cible.svg?raw';
import illuRevisions from '../../assets/illustrations/revisions-du-jour.svg?raw';
import illuPointsFaibles from '../../assets/illustrations/points-faibles.svg?raw';
import illuExplorer from '../../assets/illustrations/apprentissage-guide.svg?raw';
import { DomainesBilan } from '../../components/quiz/GuidedNextStep';
import { loginUrl } from '../../utils/loginRedirect';
import {
    Compass, Target, Loader2, ChevronRight,
    AlertTriangle, Home, ArrowLeft, Trash2, Lock, Check,
} from 'lucide-react';

/**
 * Apprentissage guidé : démo ou contenu autorisé selon les droits.
 * Bilan ciblé par catégories → carte de maîtrise → séance du jour → boucle.
 * Orchestration des briques existantes (examen-melange, smart/SM2, Quiz).
 */
const STATUS = {
    solide:       { label: 'Solide', color: '#16a34a' },
    fragile:      { label: 'Fragile', color: '#f59e0b' },
    a_travailler: { label: 'À travailler', color: '#ef4444' },
    premiere_mesure: { label: 'Première mesure', color: '#64748b' },
    non_evalue:   { label: 'Non évalué', color: '#94a3b8' },
};

// Demande de retour au choix du concours après « Changer » (survit à la recréation du composant).
let retourAuChoixParcours = false;

export default function GuidedLearning({ embedded = false, scopeSlugProp = null, onCloseEmbedded = null, parcoursSlugProp = null, autoLaunch = false, onAutoLaunched = null, onChooseParcours = null, onSubscribe = null, verdictUpsell = null }) {
    usePageTitle('Apprentissage guidé');
    useFeaturePing('guided_learning');
    const params = useParams();
    const scopeSlug = scopeSlugProp || params.scope;
    const navigate = useNavigate();
    const { user, dashboardPrefs } = useAuthStore();
    const { prefs, setParcoursForScope } = useDashboardPrefs(dashboardPrefs);
    const parcoursSlug = parcoursSlugProp ?? prefs.parcoursByScope?.[scopeSlug] ?? '';
    const { startExamenMelange, startQuiz, startSm2DueQuiz, resumeSession } = useQuizStore();
    const goBack = () => { if (embedded && onCloseEmbedded) onCloseEmbedded(); else navigate(`/compte/quiz/${scopeSlug}`); };
    const fmtDate = (s) => {
        if (!s) return '';
        const d = new Date(String(s).replace(' ', 'T'));
        return isNaN(d) ? '' : d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const [plan, setPlan] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedMats, setSelectedMats] = useState([]);
    const [showBilanSetup, setShowBilanSetup] = useState(false);
    const [launching, setLaunching] = useState(false);
    const [quizActive, setQuizActive] = useState(false);
    const [confirmReset, setConfirmReset] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [showAll, setShowAll] = useState(false);
    // Diagnostic guidé (audit en entonnoir) : balayage stratifié → verdict → zoom
    const [diagRunning, setDiagRunning] = useState(false);
    const [diagVerdict, setDiagVerdict] = useState(null); // { rows, suggested } | null

    const load = useCallback(() => {
        if (!scopeSlug) return;
        setLoading(true);
        api.get('/user/learning-plan.php', { params: { scope: scopeSlug, parcours: parcoursSlug } })
            .then(({ data }) => { setPlan(data.data); setError(null); })
            .catch(e => setError(e.response?.data?.message || 'Erreur'))
            .finally(() => setLoading(false));
    }, [scopeSlug, parcoursSlug]);
    useEffect(() => { load(); }, [load]);

    // Lancement direct depuis « Ta prochaine étape » : une seule fois, dès le plan chargé.
    // La fonction est fournie plus bas (après les gardes) via une ref.
    const autoLaunchRef = useRef(null);
    const autoLaunchDone = useRef(false);
    useEffect(() => {
        if (!autoLaunch || autoLaunchDone.current || !plan?.recommendation || plan.parcours?.requis || !autoLaunchRef.current) return;
        autoLaunchDone.current = true;
        autoLaunchRef.current();
        // La demande est consommée chez le parent : le composant est recréé à chaque changement de concours
        // (clé = parcours), et sans cela un bilan arrêté se relançait aussitôt sur le nouveau parcours (26/10).
        onAutoLaunched?.();
    }, [autoLaunch, plan]);

    // Parcours (lot A) : bilan affiché, une fois par thématique et par visite de la page
    // (le plan se recharge après chaque séance, ce n'est pas un nouvel affichage).
    const bilanSeen = useRef(null);
    useEffect(() => {
        const id = plan?.scope?.id;
        if (!id || bilanSeen.current === id) return;
        bilanSeen.current = id;
        trackEvent('bilan_viewed', { scopeId: id, source: autoLaunch === 'bilan' ? 'lien_bilan' : autoLaunch ? 'lien_direct' : undefined,
            meta: { reco: plan.recommendation?.type || null, demo_only: !!plan.demo_only, has_bilan: !!plan.has_bilan,
                // Compte gratuit : domaines faibles pour lesquels l'abonnement ajoute des questions (affichage mesuré).
                ...(plan.demo_only ? { plus_domaines: new Set((plan.sous_sujets || [])
                    .filter(s => (s.status === 'a_travailler' || s.status === 'fragile') && plan.abonnement_domaines?.[s.domaine_id])
                    .map(s => s.domaine_id)).size } : {}) } });
    }, [plan, autoLaunch]);

    // « Changer » de concours : une fois le choix réaffiché (composant recréé, clé = parcours), on le ramène à l'écran.
    const choixParcoursRef = useRef(null);
    useEffect(() => {
        if (!retourAuChoixParcours || !plan?.parcours?.requis || !choixParcoursRef.current) return;
        retourAuChoixParcours = false;
        choixParcoursRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, [plan]);

    // --- Gates ---
    if (!user) return <Navigate to={loginUrl()} replace />;
    if (!scopeSlug) return <Navigate to="/compte" replace />;

    const scope = plan?.scope;
    // Page intégrée : le choix remonte à la page (qui tient le parcours actif) ; route directe : préférence locale.
    const chooseParcours = (slug) => (onChooseParcours ? onChooseParcours(slug) : setParcoursForScope(scopeSlug, slug));
    const parcoursLabel = plan?.parcours?.actif && plan.parcours.options?.length >= 2
        ? plan.parcours.options.find(p => p.slug === plan.parcours.actif)?.label : null;
    const scopeId = scope?.id || 0;
    const scopeInfo = scope ? { id: scope.id, label: scope.label, image: scope.image, couleur_theme: scope.couleur_theme, content_source: scope.content_source } : null;
    const quizOverlayBg = scope?.image ? { backgroundImage: `url(${scope.image})` } : {};
    const isCustom = scope?.content_source === 'user';
    // Thematique publiee : voir sourceOf() dans quizStore (table partner_questions).
    const isPartner = scope?.content_source === 'partner';

    // Reprise d'audit : 100% basée sur les sessions PERSISTÉES (auto-save serveur,
    // origin 'audit-parcours') → survit au rechargement / changement d'appareil, et
    // le header ne garde aucun bandeau résiduel (le quizStore est reset à la sortie).
    // Plusieurs audits possibles (chaque tentative = une session) ; le plus récent
    // est mis en avant comme « Audit en cours ».
    const pendingAudits = plan?.pending_audits || [];

    // Reprise d'un autre audit persisté (par son id)
    const doResumeAuditSession = async (session) => {
        setLaunching(true);
        const ok = await resumeSession(session.id, { isCustom, isPartner, scopeId, parcours: parcoursSlug });
        setLaunching(false);
        if (ok) { setDiagVerdict(null); setDiagRunning(true); setQuizActive(true); }
    };
    // Annuler un audit persisté (29/10/2026) : seulement sans aucune réponse, revérifié par le serveur
    // (only_if_empty) ; un audit commencé se reprend, et « S'arrêter là » enregistre ce qui a été fait.
    const cancelAuditSession = async (session) => {
        try {
            await api.delete('/qcm/session.php', { data: { session_id: session.id, only_if_empty: true } });
        } catch (e) { console.error('cancel audit error:', e); }
        load();
    };

    // --- Launchers (réutilisent quizStore) ---
    const launchBilan = async () => {
        if (!selectedMats.length) return;
        setLaunching(true);
        const ok = await startExamenMelange(scopeId, {
            nbQuestions: 20, guided: true, parcours: parcoursSlug, categories: selectedMats, difficulteMin: 1, difficulteMax: 3,
            isCustom, isPartner, playMode: 'quiz', origin: 'audit-parcours',
        });
        setLaunching(false);
        if (ok) {
            // Referme l'écran d'audit pour retomber sur le parcours au retour du quiz.
            setShowBilanSetup(false);
            setSelectedMats([]);
            setDiagVerdict(null);
            setDiagRunning(true);
            setQuizActive(true);
        }
    };
    // Bilan court : inédites en priorité, domaines alternés, 16 questions maximum (2 par rubrique sur 8 domaines).
    const launchDiagnostic = async () => {
        setLaunching(true);
        const ok = await startExamenMelange(scopeId, {
            nbQuestions: 16, guided: true, parcours: parcoursSlug, categories: [], difficulteMin: 1, difficulteMax: 3,
            isCustom, isPartner, playMode: 'quiz', origin: 'audit-diagnostic',
        });
        setLaunching(false);
        if (ok) {
            setDiagVerdict(null);
            setDiagRunning(true);
            setQuizActive(true);
        }
    };

    // Diagnostic guidé — PHASE 2 : zoom pré-ciblé sur les catégories faibles /
    // incertaines identifiées par le balayage (= l'audit existant, pré-configuré).
    const launchApprofondir = async (matiereIds) => {
        if (!matiereIds.length) return;
        setLaunching(true);
        // Les ids viennent du bilan, donc du DOMAINE réel (matiere_ref_id) : ils
        // partent dans `categoriesRef`, pas dans `categories`, qui filtre sur la
        // matière du catalogue. Sur un scope comme DGFiP les deux diffèrent, et
        // se tromper de paramètre renvoie zéro question.
        const ok = await startExamenMelange(scopeId, {
            nbQuestions: 20, guided: true, parcours: parcoursSlug,
            ...(isCustom ? { categories: matiereIds.map(id => `ucat_${id}`) } : { categoriesRef: matiereIds }), difficulteMin: 1, difficulteMax: 3,
            isCustom, isPartner, playMode: 'quiz', origin: 'audit-parcours',
        });
        setLaunching(false);
        if (ok) {
            setDiagVerdict(null);
            setDiagRunning(true);
            setQuizActive(true);
        }
    };

    // Verdict du balayage : lu depuis le quizStore AVANT reset, à la fermeture.
    // JSP ('?') = lacune assumée → à travailler direct ; partiel = fragile (à
    // creuser) ; tout bon = solide (fragile si réponses lentes > 45 s).
    const computeDiagVerdict = () => {
        const st = useQuizStore.getState();
        if (!st.finished || !Array.isArray(st.questions)) return null;
        const cats = {};
        for (const q of st.questions) {
            // Regroupement par domaine réel : `categorie` est déjà le libellé du
            // domaine, matiere_ref_id son id (= matiere_id quand aucun domaine
            // distinct n'est déclaré).
            const domaineId = q.user_category_id ?? q.matiere_ref_id ?? q.matiere_id;
            if (domaineId == null) continue;
            const c = (cats[domaineId] ||= { matiere_id: domaineId, label: q.categorie || 'Catégorie', ok: 0, total: 0, jsp: 0, slow: 0 });
            const given = st.answers?.[q.id];
            const correct = st.correctAnswers?.[q.id]?.correct;
            const rt = st.responseTimes?.[q.id];
            c.total++;
            if (given === '?') c.jsp++;
            else if (given && correct && given === correct) { c.ok++; if (rt && rt > 45) c.slow++; }
        }
        const order = ['a_travailler', 'fragile', 'premiere_mesure', 'solide', 'non_evalue'];
        const rows = Object.values(cats).map(c => {
            let statut;
            if (c.jsp > 0 || c.ok === 0) statut = 'a_travailler';
            else if (c.ok === c.total) statut = 'premiere_mesure';
            else statut = 'fragile';
            return { ...c, statut };
        }).sort((a, b) => order.indexOf(a.statut) - order.indexOf(b.statut) || a.label.localeCompare(b.label));
        if (!rows.length) return null;
        const suggested = rows.filter(r => r.statut === 'a_travailler' || r.statut === 'fragile').map(r => r.matiere_id);
        return { rows, suggested };
    };

    const launchSession = async (ssId) => {
        setLaunching(true);
        const ok = await startQuiz(ssId, scopeId, 10, 'apprentissage', null, 'subject', { isCustom, isPartner, parcours: parcoursSlug });
        setLaunching(false);
        if (ok) setQuizActive(true);
    };
    const launchSm2 = async () => {
        setLaunching(true);
        // Nouvelle sélection sur le périmètre courant (démo/abonnement/concours).
        // Les anciennes séances restent reprenables depuis Mes révisions.
        // isCustom indispensable : sans lui, smart-answers.php interroge la table
        // éditoriale `questions` → collision d'IDs avec `user_questions` → mauvaise
        // explication affichée sur une thématique perso.
        const ok = await startSm2DueQuiz(scopeId, 20, { isCustom, isPartner, playMode: 'apprentissage', scopeImage: scope?.image, parcours: parcoursSlug, guided: true });
        setLaunching(false);
        if (ok) setQuizActive(true);
    };
    const launchRecommendation = () => {
        const r = plan?.recommendation;
        if (!r) return;
        if (r.type === 'bilan' || r.type === 'rebilan') { setShowBilanSetup(true); return; }
        if (r.type === 'sm2') return launchSm2();
        if (r.type === 'weak') return launchSession(r.sous_sujet_id);
        if (r.type === 'explore') return launchDiagnostic();
    };
    // Même règle que le bouton principal de la carte de séance : un bilan se lance
    // directement (16 questions), le choix manuel des domaines reste dans le panneau.
    autoLaunchRef.current = () => {
        const t = plan?.recommendation?.type;
        if (t === 'empty') return;
        // Lien direct ?bilan=1 (mails) : un bilan, quelle que soit la recommandation.
        if (autoLaunch === 'bilan' || t === 'bilan' || t === 'rebilan') return launchDiagnostic();
        return launchRecommendation();
    };
    const trackAction = (source) => trackEvent('bilan_action_clicked', { source, scopeId,
        meta: { reco: plan?.recommendation?.type || null } });
    const doReset = async () => {
        if (!scopeId) return;
        setResetting(true);
        try {
            await api.post('/user/reset-parcours.php', { scope_id: scopeId });
            setConfirmReset(false);
            setShowBilanSetup(false);
            setSelectedMats([]);
            load();
        } catch (e) {
            console.error('reset-parcours error:', e);
        } finally {
            setResetting(false);
        }
    };

    // --- Quiz overlay (même pattern que TabExamen) ---
    if (quizActive && scopeInfo) {
        // Toujours reset le quizStore en quittant l'audit : sinon le bandeau de quiz
        // du header (qui lit le quizStore) persiste sur la page parcours (même URL
        // …/quiz/…/guide). L'audit est auto-sauvegardé côté serveur
        // (quiz_current_sessions, origin 'audit-parcours') → la reprise passe par la
        // carte des audits sauvegardés (pendingAudits / doResumeAuditSession).
        const backToParcours = () => {
            // Diagnostic guidé : capture le verdict par catégorie AVANT le reset
            // (uniquement si le balayage a été terminé — sinon la session persiste).
            if (diagRunning) {
                setDiagVerdict(computeDiagVerdict());
                setDiagRunning(false);
            }
            useQuizStore.getState().reset();
            setQuizActive(false);
            setShowBilanSetup(false);
            load();
        };
        return (
            <div className={`quiz-modal-overlay ${quizOverlayBg.backgroundImage ? 'has-bg-image' : ''}`} style={quizOverlayBg}>
                <Quiz
                    scopeInfoProp={scopeInfo}
                    onClose={backToParcours}
                    verdictAction={{ label: 'Voir mon parcours', onClick: backToParcours }}
                    // Compte gratuit : la proposition chiffrée des onglets, aussi en fin de bilan.
                    // C'est ici que jouent les gratuits les plus engagés (cf. user 117, 4 jours sur 5
                    // sans jamais voir d'offre). Ouverte depuis le parcours, pas depuis le quiz.
                    verdictUpsell={verdictUpsell && onSubscribe ? { ...verdictUpsell, source: 'fin_quiz_guide', onClick: () => { backToParcours(); onSubscribe('fin_quiz_guide'); } } : null}
                />
            </div>
        );
    }

    const BackBtn = () => (
        <button onClick={goBack} title={embedded ? 'Retour aux choix' : 'Retour à la thématique'}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, border: '1px solid var(--card-border)', background: 'transparent', borderRadius: 8, padding: '5px 12px', cursor: 'pointer', color: 'inherit', fontSize: '0.85em' }}>
            <ArrowLeft size={15} /> {embedded ? 'Retour aux choix' : 'Retour'}
        </button>
    );
    if (loading) {
        return (
            <div style={{ maxWidth: 860, margin: '0 auto', padding: '20px 16px' }}>
                <BackBtn />
                <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}><Loader2 size={26} className="spin" /><div style={{ marginTop: 10 }}>Analyse de ton parcours…</div></div>
            </div>
        );
    }
    if (error) {
        return (
            <div style={{ maxWidth: 860, margin: '0 auto', padding: '20px 16px' }}>
                <BackBtn />
                <div style={{ padding: 40, textAlign: 'center' }}>
                    <AlertTriangle size={30} style={{ color: '#ef4444' }} />
                    <p style={{ color: 'var(--text-muted)' }}>{error}</p>
                </div>
            </div>
        );
    }
    if (!plan) return null;

    const matieres = plan.matieres || [];
    const sousSujets = plan.sous_sujets || [];

    // Indicateur live de la sélection du bilan
    const selSujets = matieres.filter(m => selectedMats.includes(m.id)).reduce((s, m) => s + m.nb_sous_sujets, 0);
    const qPerSujet = selSujets > 0 ? (20 / selSujets) : 0;

    // Vue groupée par STATUT (À travailler → Fragile → Maîtrisé → Non évalué)
    // + "à prioriser aujourd'hui" = faibles/fragiles les plus prioritaires.
    const STATUS_ORDER = ['a_travailler', 'fragile', 'solide', 'premiere_mesure', 'non_evalue'];
    const byStatus = {};
    for (const ss of sousSujets) (byStatus[ss.status] ||= []).push(ss);
    const priority = sousSujets
        .filter(ss => ss.status === 'a_travailler' || ss.status === 'fragile')
        .sort((a, b) => b.priority - a.priority)
        .slice(0, 3);
    // Compte gratuit : questions que l'abonnement ajoute par domaine de suivi (vide sinon).
    const plus = plan.abonnement_domaines || {};
    const plusVerdict = diagVerdict ? diagVerdict.suggested.reduce((n, d) => n + (plus[d]?.questions || 0), 0) : 0;
    const nbSolide = (byStatus.solide || []).length;
    const nbNonEval = (byStatus.non_evalue || []).length;
    const nbAmorce = (byStatus.premiere_mesure || []).length;

    const toggleMat = (id) => setSelectedMats(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

    const showSetup = showBilanSetup;
    // Compte gratuit : domaines du concours sans sujet d'essai (absents de la liste des matières jouables).
    const verrouillesHorsChoix = (plan.bilan_domaines?.verrouilles || []).filter(d => !matieres.some(m => m.label === d.label));

    // ===== SOUS-ÉCRAN : AUDIT (sélection des catégories) =====
    if (showSetup) {
        const nbQ = Math.min(20, matieres.filter(m => selectedMats.includes(m.id)).reduce((s, m) => s + m.nb_questions, 0));
        return (
            <div style={{ maxWidth: 860, margin: '0 auto', padding: '20px 16px 60px' }}>
                <button onClick={() => { setShowBilanSetup(false); setSelectedMats([]); }} title="Retour au parcours"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5, border: '1px solid var(--card-border)', background: 'transparent', borderRadius: 8, padding: '5px 12px', cursor: 'pointer', color: 'inherit', fontSize: '0.85em', marginBottom: 16 }}>
                    <ArrowLeft size={15} /> Retour au parcours
                </button>
                {/* Choix des domaines (refonte du 26/10/2026) : tuiles cochables, domaines de l'accès complet à part
                    pour un compte gratuit, résumé et lancement en pied. */}
                <div className="bilan-cible" style={{ '--bc-accent': scope?.couleur_theme || 'var(--primary)' }}>
                    <div className="bilan-cible__head">
                        <Illustration className="bilan-cible__illu" svg={illuBilan} color={scope?.couleur_theme} />
                        <div>
                            <h1>Bilan ciblé</h1>
                            <p>Choisis les domaines à évaluer : le bilan pioche jusqu’à 20 questions parmi eux.</p>
                        </div>
                    </div>
                    <div className="bilan-cible__section-head">
                        <h2>{plan.demo_only ? 'Domaines disponibles en accès gratuit' : 'Domaines'}</h2>
                        {matieres.some(m => m.nb_questions > 0) && (
                            <button type="button" className="guided-next__link"
                                onClick={() => setSelectedMats(selectedMats.length ? [] : matieres.filter(m => m.nb_questions > 0).map(m => m.id))}>
                                {selectedMats.length ? 'Tout désélectionner' : 'Tout sélectionner'}
                            </button>
                        )}
                    </div>
                    <div className="bilan-cible__grid">
                        {matieres.map(m => {
                            const active = selectedMats.includes(m.id);
                            const empty = m.nb_questions === 0;
                            return (
                                <button key={m.id} type="button" disabled={empty} aria-pressed={active} onClick={() => toggleMat(m.id)}
                                    className={`bilan-cible__tile${active ? ' is-active' : ''}`}>
                                    <span className="bilan-cible__check" aria-hidden="true">{active && <Check size={13} />}</span>
                                    <span className="bilan-cible__label">
                                        <strong>{m.label}</strong>
                                        <small>{m.nb_questions} question{m.nb_questions > 1 ? 's' : ''}{plan.demo_only ? ' d’essai' : ''}</small>
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                    {/* Compte gratuit : domaines du concours sans sujet d'essai, montrés plutôt que tus. */}
                    {plan.demo_only && verrouillesHorsChoix.length > 0 && (<>
                        <div className="bilan-cible__section-head">
                            <h2><Lock size={13} aria-hidden="true" /> Avec l’accès complet</h2>
                            {onSubscribe && <button type="button" className="guided-next__link" onClick={() => onSubscribe('bilan_cible')}>Voir l’accès complet</button>}
                        </div>
                        <div className="bilan-cible__grid">
                            {verrouillesHorsChoix.map(d => (
                                <div key={`verrou-${d.id}`} className="bilan-cible__tile is-locked" title="Domaine du concours sans sujet d’essai">
                                    <span className="bilan-cible__check" aria-hidden="true"><Lock size={11} /></span>
                                    <span className="bilan-cible__label">
                                        <strong>{d.label}</strong>
                                        <small>pas de sujet d’essai</small>
                                    </span>
                                </div>
                            ))}
                        </div>
                    </>)}
                    <div className="bilan-cible__foot">
                        <span className="bilan-cible__summary">
                            {selectedMats.length === 0 ? 'Aucun domaine choisi' : <>
                                {selectedMats.length} domaine{selectedMats.length > 1 ? 's' : ''} · {selSujets} sujet{selSujets > 1 ? 's' : ''} · {nbQ} questions
                                {qPerSujet > 0 && qPerSujet < 3 && <em> — moins de domaines pour approfondir chacun</em>}
                            </>}
                        </span>
                        <button className="btn btn-primary" disabled={!selectedMats.length || launching} onClick={launchBilan}>
                            {launching ? <Loader2 size={14} className="spin" /> : <Target size={14} />} Lancer le bilan{selectedMats.length ? ` (${nbQ} questions)` : ''}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ===== ÉCRAN PARCOURS (home, toujours affiché) =====
    return (
        <div style={embedded ? { padding: '2px 0 24px' } : { maxWidth: 860, margin: '0 auto', padding: '20px 16px 60px' }}>
            {/* Retour : vers la thématique (page) ou vers la progression (inline) */}
            <button onClick={goBack} title={embedded ? 'Retour aux choix' : 'Retour à la thématique'}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, border: '1px solid var(--card-border)', background: 'transparent', borderRadius: 8, padding: '5px 12px', cursor: 'pointer', color: 'inherit', fontSize: '0.85em', marginBottom: 14 }}>
                <ArrowLeft size={15} /> {embedded ? 'Retour aux choix' : `Retour à ${scope?.label || 'la thématique'}`}
            </button>
            {/* Header — le label de thématique est masqué en inline (déjà affiché par la page) */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: embedded ? 16 : 4, flexWrap: 'wrap' }}>
                <Illustration className="guided-title__illu" svg={illuExplorer} color={scope?.couleur_theme} />
                <h1 style={{ margin: 0, fontSize: '1.4em' }}>Apprentissage guidé</h1>
                <span style={{ fontSize: '0.7em', fontWeight: 700, color: '#fff', background: '#16a34a', borderRadius: 999, padding: '2px 8px' }}>NOUVEAU</span>
            </div>
            {!embedded && <p style={{ color: 'var(--text-muted)', marginTop: 0 }}>{scope?.label}</p>}
            {/* Après un bilan : un cadre regroupe le concours, l'avancement, la maîtrise, la séance et les autres actions,
                pour les séparer des sujets à prioriser (retour du 26/10/2026). */}
            <div className={!!Number(plan.has_bilan) && !plan.parcours?.requis ? 'guided-home-panel' : undefined}>
            {parcoursLabel && (
                <p className="guided-next__parcours" style={{ marginTop: 0 }}>
                    Bilan et séances sur le concours : <strong>{parcoursLabel}</strong>
                    <button type="button" className="guided-next__link" onClick={() => { retourAuChoixParcours = true; chooseParcours(''); }}>Changer</button>
                </p>
            )}

            <p style={{ color: 'var(--text-muted)' }}>
                {plan.demo_only ? 'Parcours gratuit limité aux sujets de démonstration. ' : ''}
                {!sousSujets.some(s => s.total > 0) ? 'Aucune question n’est disponible dans ce parcours pour le moment.' : plan.exploration_remaining > 0 ? `${plan.exploration_remaining} questions restent à découvrir. Les prochains bilans privilégient les questions jamais vues.` : 'Toutes les questions accessibles ont été rencontrées. Tu peux réévaluer tes connaissances.'}
            </p>

            {/* Verdict du diagnostic guidé (phase 1) : bilan par catégorie + zoom proposé */}
            {diagVerdict && (
                <div style={{ border: `1.5px solid ${scope?.couleur_theme || 'var(--primary)'}`, borderRadius: 14, padding: '16px 18px', marginBottom: 16, background: 'var(--card-bg)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                        <Compass size={16} style={{ color: scope?.couleur_theme || 'var(--primary)' }} />
                        <strong>Premiers repères — domaines rencontrés dans ce bilan</strong>
                    </div>
                    <div style={{ display: 'grid', gap: 6, marginBottom: 12 }}>
                        {diagVerdict.rows.map(r => (
                            <div key={r.matiere_id} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.88em' }}>
                                <span style={{ width: 9, height: 9, borderRadius: '50%', background: STATUS[r.statut]?.color || '#94a3b8', flexShrink: 0 }} />
                                <span style={{ flex: 1, fontWeight: 600 }}>
                                    {r.label}
                                    {(r.statut === 'a_travailler' || r.statut === 'fragile') && plus[r.matiere_id] && (
                                        <span style={{ display: 'block', fontWeight: 400, fontSize: '0.88em', color: 'var(--text-muted)' }}>
                                            +{plus[r.matiere_id].questions} questions avec l’abonnement
                                        </span>
                                    )}
                                </span>
                                <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.9em', color: 'var(--text-muted)' }}>
                                    {r.ok}/{r.total}
                                    {r.jsp > 0 && <span style={{ color: '#f59e0b' }}> · {r.jsp} « je ne sais pas »</span>}
                                    {r.slow > 0 && <span> · lent</span>}
                                </span>
                                <span style={{ fontSize: '0.82em', fontWeight: 700, color: STATUS[r.statut]?.color }}>{STATUS[r.statut]?.label}</span>
                            </div>
                        ))}
                        {/* Compte gratuit : un domaine non tiré n'est jamais noté (ni zéro, ni « à travailler »). */}
                        {(plan.bilan_domaines?.accessibles || [])
                            .filter(d => !diagVerdict.rows.some(r => String(r.matiere_id) === String(d.id)))
                            .map(d => (
                                <div key={`absent-${d.id}`} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.88em', color: 'var(--text-muted)' }}>
                                    <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#cbd5e1', flexShrink: 0 }} />
                                    <span style={{ flex: 1, fontWeight: 600 }}>{d.label}</span>
                                    <span style={{ fontSize: '0.82em', fontWeight: 600 }}>Non évalué dans ce bilan</span>
                                </div>
                            ))}
                        {(plan.bilan_domaines?.verrouilles || []).map(d => (
                            <div key={`verrou-${d.id}`} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.88em', color: 'var(--text-muted)' }}>
                                <Lock size={11} style={{ flexShrink: 0 }} aria-hidden="true" />
                                <span style={{ flex: 1, fontWeight: 600 }}>{d.label}</span>
                                <span style={{ fontSize: '0.82em', fontWeight: 600 }}>Non évalué — accès complet</span>
                            </div>
                        ))}
                    </div>
                    {plan.bilan_domaines?.verrouilles?.length > 0 && onSubscribe && (
                        <div style={{ marginBottom: 12 }}>
                            <button type="button" className="btn btn-sm" onClick={() => onSubscribe('bilan_complet')}
                                style={{ border: `1.5px solid ${scope?.couleur_theme || 'var(--primary)'}`, color: scope?.couleur_theme || 'var(--primary)', background: 'transparent' }}>
                                <Lock size={13} /> Débloquer le bilan complet
                            </button>
                        </div>
                    )}
                    {diagVerdict.suggested.length > 0 ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                            <button className="btn btn-primary btn-sm" disabled={launching} onClick={() => launchApprofondir(diagVerdict.suggested)}>
                                {launching ? <Loader2 size={14} className="spin" /> : <Target size={14} />} Travailler ces domaines ({diagVerdict.suggested.length} catégorie{diagVerdict.suggested.length > 1 ? 's' : ''})
                            </button>
                            <button className="btn btn-ghost btn-sm" onClick={() => setDiagVerdict(null)} style={{ color: 'var(--text-muted)' }}>
                                Plus tard
                            </button>
                            {plusVerdict > 0 && onSubscribe && (
                                <button type="button" className="guided-next__link" onClick={() => onSubscribe('bilan_verdict')}>
                                    Voir l’abonnement : {plusVerdict} questions de plus sur ces domaines
                                </button>
                            )}
                        </div>
                    ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', fontSize: '0.88em' }}>
                            <span>Toutes les réponses de ce bilan sont justes. Explore la suite pour compléter ces premiers repères.</span>
                            <button className="btn btn-ghost btn-sm" onClick={() => setDiagVerdict(null)} style={{ color: 'var(--text-muted)' }}>Fermer</button>
                        </div>
                    )}
                </div>
            )}

            {/* Audits sauvegardés (auto-save serveur) — le plus récent en avant
                (« Audit en cours »), tous reprenables/annulables via la session persistée. */}
            {pendingAudits.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                    {pendingAudits.map((a, i) => (
                        <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 12,
                            background: i === 0 ? 'rgba(245,158,11,0.1)' : 'var(--card-bg)',
                            border: i === 0 ? '1px solid #f59e0b' : '1px solid var(--card-border)',
                            borderRadius: 12, padding: '12px 16px', marginBottom: 8 }}>
                            <span style={{ fontSize: '1.25em' }}>{i === 0 ? '⏸️' : '🧭'}</span>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 700, fontSize: '0.9em' }}>{i === 0 ? 'Audit en cours' : 'Audit sauvegardé'}</div>
                                <div style={{ fontSize: '0.8em', color: 'var(--text-muted)' }}>
                                    {a.nb_answered}/{a.nb_questions} répondues{i === 0 ? ' — reprends là où tu t\'es arrêté.' : ` · ${fmtDate(a.updated_at)}`}
                                </div>
                            </div>
                            <button className="btn btn-primary btn-sm" disabled={launching} onClick={() => doResumeAuditSession(a)} style={{ flexShrink: 0 }}>
                                {launching ? <Loader2 size={14} className="spin" /> : <>Reprendre <ChevronRight size={14} /></>}
                            </button>
                            {Number(a.nb_answered) === 0 && (
                                <button className="btn btn-ghost btn-sm" title="Annuler cet audit (aucune réponse)" onClick={() => cancelAuditSession(a)} style={{ flexShrink: 0, color: 'var(--danger, #ef4444)' }}>
                                    <Trash2 size={14} />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* ===== HERO : maîtrise (donut) + séance du jour ===== */}
            <div className="guided-overview">
                {!!Number(plan.has_bilan) && <div className="guided-mastery">
                    {plan.global_mastery != null ? (
                        <>
                            <MasteryDonut value={plan.global_mastery ?? 0} />
                            <div>
                                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '1.25em', color: 'var(--heading)' }}>Ta maîtrise</div>
                                {plan.mastery_trend != null && plan.mastery_trend !== 0 && (
                                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 8, padding: '4px 11px', borderRadius: 999, fontWeight: 700, fontSize: '0.88em',
                                        background: plan.mastery_trend > 0 ? 'var(--alert-success-bg)' : 'var(--alert-error-bg)',
                                        color: plan.mastery_trend > 0 ? 'var(--alert-success-color)' : 'var(--alert-error-color)' }}>
                                        {plan.mastery_trend > 0 ? '▲' : '▼'} {plan.mastery_trend > 0 ? '+' : ''}{plan.mastery_trend}% cette semaine
                                    </div>
                                )}
                                {plan.last_audit_at && (
                                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8em', color: 'var(--text-tertiary, #94a3b8)', marginTop: 12 }}>
                                        Dernier audit · {fmtDate(plan.last_audit_at)}
                                    </div>
                                )}
                            </div>
                            <Illustration className="guided-mastery__illu" svg={illuMaitrise} color={scope?.couleur_theme} />
                        </>
                    ) : (
                        <>
                            <div style={{ width: 56, height: 56, borderRadius: 14, background: 'rgba(139,92,246,0.12)', color: '#8B5CF6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <Illustration svg={illuExplorer} className="content-scope-illu" color={scope?.couleur_theme} />
                            </div>
                            <div>
                                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '1.1em', color: 'var(--heading)' }}>{plan.has_bilan ? 'Premiers repères enregistrés' : 'Fais ton premier bilan'}</div>
                                <div style={{ fontSize: '0.85em', color: 'var(--text-muted)', marginTop: 4 }}>{plan.has_bilan ? 'Complète ton bilan pour estimer ta maîtrise par sujet.' : 'Un bilan court pour commencer à orienter tes révisions.'}</div>
                            </div>
                        </>
                    )}
                </div>}
                {plan.parcours?.requis ? (
                    <section className="guided-session" aria-labelledby="guided-parcours-title" ref={choixParcoursRef}>
                        <div>
                            <p className="guided-session-eyebrow">AVANT TON BILAN</p>
                            <h2 id="guided-parcours-title">Tu prépares quel concours ?</h2>
                            <p className="guided-session-description">Ton bilan et tes séances porteront uniquement sur ce concours. Tu pourras changer à tout moment.</p>
                        </div>
                        <div className="guided-session-actions">
                            {plan.parcours.options.map(p => (
                                <button key={p.slug} className="guided-session-primary" onClick={() => chooseParcours(p.slug)}>{p.label} <ChevronRight size={16} /></button>
                            ))}
                        </div>
                    </section>
                ) : (
                <SessionCard plan={plan} launching={launching}
                    onLaunch={() => { trackAction('recommandation'); launchRecommendation(); }}
                    onLaunchDiagnostic={() => { trackAction('diagnostic'); launchDiagnostic(); }}
                    onChooseDomains={() => { trackAction('choix_domaines'); setShowBilanSetup(true); }} canChooseDomains={matieres.length > 0} />
                )}
            </div>
            {!!Number(plan.has_bilan) && <div className="guided-actions" aria-label="Autres façons de travailler">
                {!['bilan', 'explore', 'rebilan', 'empty'].includes(plan.recommendation?.type) &&
                    <button className="btn btn-ghost" disabled={launching || !sousSujets.some(s => s.total > 0)} onClick={() => { trackAction('diagnostic'); launchDiagnostic(); }}>
                        <Compass size={16} /> {plan.exploration_remaining > 0 ? 'Explorer la suite' : 'Refaire un bilan'} · 16 questions maximum{plan.demo_only && <sup className="guided-renvoi" aria-hidden="true">*</sup>}
                    </button>}
                <button className="btn btn-ghost" disabled={launching || !matieres.length} onClick={() => { trackAction('choix_domaines'); setShowBilanSetup(true); }}>Choisir moi-même les domaines</button>
            </div>}
            {!!Number(plan.has_bilan) && plan.demo_only && <LimiteGratuite plan={plan} scope={scope} onSubscribe={onSubscribe} source="bilan_actions" />}
            </div>

            {/* ===== À PRIORISER AUJOURD'HUI ===== */}
            {priority.length > 0 && (
                <div style={{ marginBottom: 26 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.2em', margin: 0, color: 'var(--heading)' }}>📌 À prioriser aujourd'hui</h2>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8em', color: 'var(--text-muted)' }}>{priority.length} sous-sujet{priority.length > 1 ? 's' : ''}</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {priority.map(ss => (
                            <PriorityCard key={ss.id} ss={ss} launching={launching} onWork={() => { trackAction('priorite'); launchSession(ss.id); }}
                                plus={plus[ss.domaine_id]} onSubscribe={onSubscribe ? () => onSubscribe('bilan_priorite') : null} />
                        ))}
                    </div>
                </div>
            )}

            {/* ===== LE RESTE DU PARCOURS (replié → vue complète par statut) ===== */}
            <div style={{ background: 'var(--card-bg)', border: '1px dashed var(--card-border)', borderRadius: 14, overflow: 'hidden' }}>
                <button onClick={() => setShowAll(v => !v)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '18px 22px', background: 'transparent', border: 'none', cursor: 'pointer', color: 'inherit', textAlign: 'left' }}>
                    <div>
                        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '1.05em', color: 'var(--heading)' }}>Le reste de ton parcours</div>
                        <div style={{ fontSize: '0.85em', color: 'var(--text-muted)', marginTop: 3 }}>
                            {nbNonEval > 0 ? `${nbNonEval} non évalué${nbNonEval > 1 ? 's' : ''}` : 'tout est évalué'}{nbAmorce > 0 ? ` · ${nbAmorce} en première mesure` : ''}{nbSolide > 0 ? ` · ${nbSolide} maîtrisé${nbSolide > 1 ? 's' : ''}` : ''} · vue complète par statut
                        </div>
                    </div>
                    <span style={{ color: 'var(--primary)', fontWeight: 700, fontSize: '0.92em', flexShrink: 0 }}>{showAll ? 'Réduire ▴' : 'Tout afficher ▾'}</span>
                </button>
                {showAll && (
                    <div style={{ padding: '0 22px 22px' }}>
                        {STATUS_ORDER.map(stKey => {
                            const items = byStatus[stKey] || [];
                            if (!items.length) return null;
                            const st = STATUS[stKey];
                            return (
                                <div key={stKey} style={{ marginBottom: 18 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 8 }}>
                                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: st.color, flexShrink: 0 }} />
                                        <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '0.95em', color: 'var(--heading)' }}>{st.label}</span>
                                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8em', color: 'var(--text-tertiary, #94a3b8)' }}>{items.length}</span>
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {items.map(ss => (
                                            <StatusRow key={ss.id} ss={ss} launching={launching} onWork={() => { trackAction('sujet'); launchSession(ss.id); }} />
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Réinitialiser le parcours (BÊTA) — destructif, self-scoped */}
            <div style={{ marginTop: 28, textAlign: 'center' }}>
                <button className="btn btn-ghost btn-sm" style={{ color: 'var(--danger, #ef4444)' }} onClick={() => setConfirmReset(true)}>
                    <Trash2 size={13} /> Réinitialiser mon parcours
                </button>
            </div>

            <ConfirmDialog
                open={confirmReset}
                title="Réinitialiser ton parcours ?"
                message={<>
                    Cela efface <strong>tout ton historique sur cette thématique</strong> (réponses, sessions, révisions espacées).
                    <br />La carte de maîtrise repartira de zéro. <strong>Action irréversible.</strong>
                </>}
                confirmLabel={resetting ? 'Réinitialisation…' : 'Réinitialiser'}
                cancelLabel="Annuler"
                variant="danger"
                onConfirm={doReset}
                onCancel={() => setConfirmReset(false)}
            />
        </div>
    );
}

// Une action recommandée ; le choix manuel des domaines reste secondaire.
function SessionCard({ plan, launching, onLaunch, onLaunchDiagnostic, onChooseDomains, canChooseDomains }) {
    const r = plan?.recommendation || { type: 'bilan' };
    const MAP = {
        bilan:   { title: 'Fais le point sur tes connaissances', sub: 'Un quiz mélangé pour repérer tes points d’appui et les notions à revoir.', cta: 'Commencer mon bilan · 16 questions maximum' },
        rebilan: { title: 'Refais le point', sub: 'Réévalue tes connaissances avec un nouveau bilan.', cta: 'Refaire un bilan · 16 questions maximum' },
        sm2:     { title: 'Révisions du jour', sub: `${r.nb || 0} questions qui reviennent au bon moment.`, cta: 'Réviser maintenant' },
        weak:    { title: `À travailler : ${r.label || ''}`, sub: 'Le sujet le plus prioritaire selon ton bilan.', cta: 'Démarrer la séance' },
        explore: { title: 'Complète ton bilan', sub: 'Des sujets restent à découvrir ou à approfondir.', cta: 'Explorer la suite · 16 questions maximum' },
        empty: { title: 'Aucun sujet accessible', sub: 'Ce parcours ne contient pas encore de questions disponibles.', cta: 'Aucune séance disponible' },
    };
    const c = MAP[r.type] || MAP.bilan;
    // Dessin de la séance (docs/img/illustrations-menus-svg).
    const illu = { sm2: illuRevisions, weak: illuPointsFaibles, explore: illuExplorer }[r.type] || (r.type === 'empty' ? null : illuBilan);
    const isBilan = r.type === 'bilan' || r.type === 'rebilan' || !MAP[r.type];
    const firstBilan = !plan.has_bilan && isBilan;
    return (
        <section className={`guided-session${firstBilan ? ' guided-session--first' : ''}${illu ? ' has-illu' : ''}`} aria-labelledby="guided-session-title">
            {illu && <Illustration className="guided-session__illu" svg={illu} color={plan.scope?.couleur_theme} />}
            <div>
                <p className="guided-session-eyebrow">{firstBilan ? 'POUR COMMENCER' : 'TA PROCHAINE SÉANCE'}</p>
                <h2 id="guided-session-title">{c.title}</h2>
                <p className="guided-session-description">{c.sub}</p>
                {/* Tableau des domaines du bilan : ✓ évalués, ✗ accès complet (compte gratuit) ; abonné : tout coché. */}
                {(isBilan || r.type === 'explore') && <DomainesBilan domaines={plan.bilan_domaines} sombre />}
            </div>
            {firstBilan && <ol className="guided-bilan-steps" aria-label="Comment se déroule le bilan">
                <li><strong>Réponds</strong><span>Jusqu’à 16 questions variées.</span></li>
                <li><strong>Fais le point</strong><span>Tes premiers repères par domaine.</span></li>
                <li><strong>Poursuis</strong><span>Une révision adaptée à tes réponses.</span></li>
            </ol>}
            <div className="guided-session-actions">
                <button className="guided-session-primary" onClick={isBilan ? onLaunchDiagnostic : onLaunch} disabled={launching || r.type === 'empty'}>
                    {launching ? <Loader2 size={16} className="spin" /> : <>{c.cta} <ChevronRight size={16} /></>}
                </button>
                {firstBilan && <div className="guided-session-alternative">
                    <span>Tu préfères cibler ton bilan ?</span>
                    <button onClick={onChooseDomains} disabled={launching || !canChooseDomains}>Choisir moi-même les domaines</button>
                </div>}
            </div>
        </section>
    );
}

// Donut de maîtrise (dégradé marque).
function MasteryDonut({ value = 0, size = 132 }) {
    const v = Math.max(0, Math.min(100, Math.round(value)));
    const r = 56, circ = 2 * Math.PI * r;
    return (
        <svg width={size} height={size} viewBox="0 0 144 144" style={{ flex: 'none' }} aria-hidden="true">
            <defs>
                <linearGradient id="gl-donut" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#7c3aed" />
                    <stop offset="100%" stopColor="#2563eb" />
                </linearGradient>
            </defs>
            <circle cx="72" cy="72" r={r} fill="none" stroke="var(--bg-subtle, #eef1f5)" strokeWidth="13" />
            <circle cx="72" cy="72" r={r} fill="none" stroke="url(#gl-donut)" strokeWidth="13" strokeLinecap="round"
                strokeDasharray={circ} strokeDashoffset={circ * (1 - v / 100)} transform="rotate(-90 72 72)"
                style={{ transition: 'stroke-dashoffset 0.7s ease' }} />
            <text x="72" y="74" textAnchor="middle" dominantBaseline="middle" fontFamily="var(--font-display)" fontWeight="800" fontSize="34" fill="var(--heading)">{v}%</text>
            <text x="72" y="98" textAnchor="middle" fontFamily="var(--font-body)" fontSize="10" fill="var(--text-tertiary, #94a3b8)" letterSpacing="1">MAÎTRISE</text>
        </svg>
    );
}

// "à revoir aujourd'hui / demain / dans N j / le JJ/MM" depuis une date SM2.
function dueLabel(nextReview) {
    if (!nextReview) return null;
    const s = String(nextReview);
    const d = new Date(s.length <= 10 ? s + 'T00:00:00' : s.replace(' ', 'T'));
    if (isNaN(d)) return null;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const diff = Math.round((d - today) / 86400000);
    if (diff <= 0) return 'à revoir aujourd\'hui';
    if (diff === 1) return 'à revoir demain';
    if (diff <= 14) return `à revoir dans ${diff} j`;
    return `à revoir le ${d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}`;
}

// Carte "à prioriser" (bordure couleur statut + barre + score + à revoir).
function PriorityCard({ ss, launching, onWork, plus = null, onSubscribe = null }) {
    const st = STATUS[ss.status];
    const pct = Math.round((ss.solidity || 0) * 100);
    const score10 = Math.round((ss.accuracy || 0) * 10);
    const due = dueLabel(ss.next_review);
    return (
        <div style={{ background: 'var(--card-bg)', border: '1px solid var(--card-border)', borderLeft: `4px solid ${st.color}`, borderRadius: 14, padding: '18px 22px', display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap' }}>
            <div style={{ flex: '1 1 220px', minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '1.05em', color: 'var(--heading)' }}>{ss.label}</span>
                    <span style={{ fontSize: '0.8em', fontWeight: 700, color: st.color }}>{st.label}</span>
                </div>
                <div style={{ height: 9, borderRadius: 999, background: 'var(--stat-bar-bg, #f1f5f9)', marginTop: 12, maxWidth: 420, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', borderRadius: 999, background: st.color }} />
                </div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8em', color: 'var(--text-tertiary, #94a3b8)', marginTop: 10 }}>
                    score {score10}/10{due ? ` · ${due}` : ''}
                </div>
                {plus && (
                    <div style={{ fontSize: '0.82em', color: 'var(--text-muted)', marginTop: 6 }}>
                        {plus.label} : +{plus.questions} questions avec l’abonnement ({plus.sujets} sujet{plus.sujets > 1 ? 's' : ''})
                        {onSubscribe && <> · <button type="button" className="guided-next__link" onClick={onSubscribe}>voir l’offre</button></>}
                    </div>
                )}
            </div>
            <button onClick={onWork} disabled={launching}
                style={{ flexShrink: 0, padding: '11px 22px', borderRadius: 10, border: 'none', cursor: launching ? 'default' : 'pointer', background: 'var(--gradient-brand)', color: '#fff', fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '0.95em', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                {launching ? <Loader2 size={16} className="spin" /> : <>Travailler <ChevronRight size={15} /></>}
            </button>
        </div>
    );
}

// Ligne compacte dans la vue groupée par statut.
function StatusRow({ ss, launching, onWork }) {
    const st = STATUS[ss.status];
    const pct = Math.round((ss.solidity || 0) * 100);
    const evalued = ss.status !== 'non_evalue' && ss.status !== 'premiere_mesure';
    const due = dueLabel(ss.next_review);
    const canWork = ss.status === 'a_travailler' || ss.status === 'fragile';
    // Un sujet effleuré porte une date de révision SM-2 alors qu'il n'est pas
    // encore jugeable : on affiche le nombre de réponses AVEC la date, sinon les
    // deux informations se contredisent (« Non évalué » et « à revoir demain »).
    const seen = ss.attempts > 0 ? `${ss.attempts} vue${ss.attempts > 1 ? 's' : ''}` : null;
    const rightLabel = evalued ? `${pct}%` : [seen, due].filter(Boolean).join(' · ') || '—';
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'var(--bg-subtle)', borderRadius: 10, padding: '10px 14px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: '0.9em', marginBottom: evalued ? 5 : 0 }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text)' }}>{ss.label}</span>
                    <span style={{ flexShrink: 0, fontFamily: 'var(--font-mono)', fontSize: '0.82em', color: 'var(--text-tertiary, #94a3b8)' }}>{rightLabel}</span>
                </div>
                {evalued && (
                    <div style={{ height: 5, borderRadius: 999, background: 'var(--stat-bar-bg, #e2e8f0)', overflow: 'hidden' }}>
                        <div style={{ width: `${pct}%`, height: '100%', background: st.color }} />
                    </div>
                )}
            </div>
            {canWork && (
                <button onClick={onWork} disabled={launching} className="btn btn-ghost btn-sm" style={{ flexShrink: 0, color: st.color, fontWeight: 600 }}>
                    Travailler <ChevronRight size={13} />
                </button>
            )}
        </div>
    );
}

// Compte gratuit : rappel que le bilan, « Explorer la suite » et le choix des domaines ne piochent que dans
// les sujets d'essai (LearningPool, demo_only) ; nombre de domaines du concours couverts quand le parcours en a.
function LimiteGratuite({ plan, scope, onSubscribe, source, choix = false }) {
    const acc = plan.bilan_domaines?.accessibles?.length || 0;
    const tot = acc + (plan.bilan_domaines?.verrouilles?.length || 0);
    const couverture = tot > 0 && acc < tot ? ` : ${acc} domaine${acc > 1 ? 's' : ''} du concours sur ${tot}` : '';
    return (
        <p className="guided-limite">
            {choix ? <Lock size={13} aria-hidden="true" /> : <span className="guided-renvoi" aria-hidden="true">*</span>}
            <span>
                {choix ? 'Compte gratuit : tu choisis parmi les sujets d’essai' : 'Compte gratuit : tes bilans et « Explorer la suite » portent sur les sujets d’essai'}{couverture}.
                {onSubscribe && <> <button type="button" className="guided-next__link" onClick={() => onSubscribe(source)}
                    style={{ color: scope?.couleur_theme || 'var(--primary)' }}>Voir l’accès complet</button></>}
            </span>
        </p>
    );
}
